<script lang="ts">
    import { CountryNull, CountryUseCurrent, slugify } from '$lib/UrlHelper.js';
    import logoPng from '$assets/logo.png';
    import logoWebp from '$assets/logo.webp';
    import logoGaoAfriquePng from '$assets/logo-gao-afrique.png';
    import logoGaoAfriqueWebp from '$assets/logo-gao-afrique.webp';
    import Picture from '$components/Picture.svelte';
    import { getContainer } from '$components/Context/ContainerContext.js';
    import DropdownButtonNavbar from '$components/Navbar/DropdownButtonNavbar.svelte';
    import {
        MyNotificationsCountQueryDocument,
        Navbar_MyNotificationsQueryDocument,
        type NavbarQueryQuery,
        Notification_MarkAllNotificationsDeliveredMutationDocument,
    } from '$gql/graphql.js';
    import { type Service, services } from './services';
    import { roundedIcon } from '$components/CountryPicture.svelte';
    import { match } from 'ts-pattern';
    import GridDropdown from '$components/Navbar/GridDropdown.svelte';
    import * as t from '$trans';
    import GridDropdownItem from '$components/Navbar/GridDropdownItem.svelte';
    import ButtonNavbar from '$components/Navbar/ButtonNavbar.svelte';
    import LoginFormDropdown from '$components/LoginForm/LoginFormDropdown.svelte';
    import MainButtonMobile from '$components/Navbar/MainButtonMobile.svelte';
    import UserDropdown from '$components/Navbar/UserDropdown.svelte';
    import NotificationsDropdown from '$components/Navbar/NotificationsDropdown.svelte';
    import { documentName } from '$gql/documentName.js';
    import SuperAppsDropdown from '$components/Navbar/SuperAppsDropdown.svelte';

    export let navbar: NavbarQueryQuery;
    export let myNotificationsCount: number;
    export let navbarCountryLink: { withCountry: string; withoutCountry: string } | null;
    export let withRightButtons: boolean = true;

    const {
        urlHelper,
        countryHelper,
        intl,
        dpapHelper,
        searchHeaderStore,
        inboxStore,
        apollo,
        mercureManager,
        lang,
    } = getContainer();

    const currentCountry = countryHelper().currentCountry();

    function serviceLink(service: Service) {
        return match(service.link)
            .with('cmsCountry', () => navbar.urls.cmsCountry)
            .otherwise((link) => link(urlHelper()));
    }

    const {
        withSearchButtonInNavBar,
        displayOnDesktop: searchHeaderDisplayOnDesktop,
        displayOnMobile: searchHeaderDisplayOnMobile,
    } = searchHeaderStore();

    const nbUnreadThreads = inboxStore().nbUnreadThreadsStore();

    mercureManager()
        .inSvelte()
        .onPrivateEvent('notification/new-notification', () => {
            apollo()
                .refetchQueries({
                    include: [
                        documentName(Navbar_MyNotificationsQueryDocument),
                        documentName(MyNotificationsCountQueryDocument),
                    ],
                })
                .catch(console.error);
        });

    async function onClickNotifications() {
        await apollo().mutate({
            mutation: Notification_MarkAllNotificationsDeliveredMutationDocument,
            refetchQueries: [documentName(MyNotificationsCountQueryDocument)],
            awaitRefetchQueries: true,
        });
    }
</script>

<header class="hidden ls:block bg-brand-blue h-navbar z-navbar relative">
    <div class="container h-full">
        <div class="flex -mx-8 h-full">
            <a
                href={urlHelper().homepage({ _country: CountryNull })}
                class="flex items-center"
                data-sveltekit-reload={false}
            >
                <Picture
                    webp={logoWebp}
                    png={logoPng}
                    class="w-[100px] h-auto"
                    alt="Logo GoAfrica"
                />
            </a>
            <nav class="flex flex-1 items-center px-4">
                <DropdownButtonNavbar
                    label={$currentCountry == null
                        ? t.navbar.countries({}, $lang)
                        : intl().countryLabel($currentCountry)}
                    iconClass={$currentCountry == null ? 'tnp tnp-africa' : null}
                    iconUrl={$currentCountry != null
                        ? roundedIcon($currentCountry)
                        : null}
                    positioning={{ placement: 'bottom-start' }}
                    withCaret={true}
                >
                    <GridDropdown
                        slot="menu"
                        let:meltItem
                        title={t.navbar.select_country({}, $lang)}
                        nbCols={9}
                    >
                        <GridDropdownItem
                            meltElement={meltItem}
                            href={navbarCountryLink?.withoutCountry ??
                                urlHelper().homepage({ _country: CountryNull })}
                            image={`/next-static/images/flags/rounded-africa.svg`}
                            label={t.navbar.africa({}, $lang)}
                        />
                        {#each [...countryHelper().sortCountries(countryHelper().modalCountries)] as c}
                            <GridDropdownItem
                                meltElement={meltItem}
                                href={navbarCountryLink?.withCountry
                                    ?.replace('__country__', c.toLowerCase())
                                    .replace(
                                        '__country_dp__',
                                        slugify(dpapHelper().replaceDpAp(c, '%dp%')),
                                    )
                                    .replace(
                                        '__country_ap__',
                                        slugify(dpapHelper().replaceDpAp(c, '%ap%')),
                                    ) ?? urlHelper().homepage({ _country: c })}
                                image={roundedIcon(c)}
                                label={intl().countryLabel(c)}
                            />
                        {/each}
                    </GridDropdown>
                </DropdownButtonNavbar>

                <ButtonNavbar
                    htmlElement="a"
                    href={navbar.me
                        ? urlHelper().homepage({ _country: CountryNull })
                        : urlHelper().explorer({ _country: CountryUseCurrent })}
                    reload={false}
                    label={t.navbar.network({}, $lang)}
                />

                <ButtonNavbar
                    htmlElement="a"
                    href={urlHelper().categories({
                        _country: CountryUseCurrent,
                        lang: $lang,
                    })}
                    reload={false}
                    label={t.navbar.companies({}, $lang)}
                />

                <ButtonNavbar
                    htmlElement="a"
                    href={urlHelper().searchUsers({})}
                    reload={false}
                    label={t.navbar.talents({}, $lang)}
                />

                <ButtonNavbar
                    htmlElement="a"
                    href={urlHelper().searchJobs({})}
                    reload={false}
                    label={t.navbar.job({}, $lang)}
                />

                <DropdownButtonNavbar
                    label={t.navbar.services({}, $lang)}
                    withCaret={true}
                >
                    <GridDropdown
                        slot="menu"
                        let:meltItem
                        title={t.navbar.our_services({}, $lang)}
                        nbCols={4}
                    >
                        {#each services as s}
                            <GridDropdownItem
                                meltElement={meltItem}
                                href={serviceLink(s)}
                                image={s.image}
                                label={s.navbarTitle ?? s.title}
                                svelteKitReload={s.sveltekitReload}
                            />
                        {/each}
                    </GridDropdown>
                </DropdownButtonNavbar>

                <ButtonNavbar
                    htmlElement="a"
                    href={urlHelper().contact({ lang: $lang })}
                    reload={false}
                    label={t.navbar.contact({}, $lang)}
                />
            </nav>

            {#if withRightButtons}
                <nav class="flex items-center">
                    {#if navbar.me == null}
                        <div class="px-[8px]">
                            <a
                                data-sveltekit-reload={false}
                                href={urlHelper().registration()}
                                class="btn-leo btn-leo-navbar-white btn-lg"
                                >{t.navbar.signup({}, $lang)}</a
                            >
                        </div>
                    {/if}

                    <DropdownButtonNavbar
                        iconClass="tnp tnp-apps-9"
                        positioning={{ placement: 'bottom-end' }}
                    >
                        <SuperAppsDropdown slot="menu" let:meltMenu {meltMenu} {navbar} />
                    </DropdownButtonNavbar>

                    {#if $withSearchButtonInNavBar}
                        <ButtonNavbar
                            onClick={(event) => {
                                event.preventDefault();
                                $searchHeaderDisplayOnDesktop =
                                    !$searchHeaderDisplayOnDesktop;
                            }}
                            srLabel={t.ui.search({}, $lang)}
                            iconClass="tnp tnp-search"
                        />
                    {/if}

                    {#if navbar.me == null}
                        <DropdownButtonNavbar
                            iconClass="tnp tnp-user-alt"
                            positioning={{ placement: 'bottom-end' }}
                        >
                            <LoginFormDropdown slot="popover" />
                        </DropdownButtonNavbar>
                    {:else}
                        <ButtonNavbar
                            htmlElement="a"
                            reload={false}
                            href={urlHelper().inbox()}
                            srLabel={t.navbar.messaging_x(
                                { nb: $nbUnreadThreads },
                                $lang,
                            )}
                            iconClass="tnp tnp-envelope-alt"
                            countBadge={$nbUnreadThreads <= 0 ? null : $nbUnreadThreads}
                        />

                        <DropdownButtonNavbar
                            iconClass="tnp tnp-bell"
                            srLabel={t.navbar.notification({}, $lang)}
                            positioning={{ placement: 'bottom-end' }}
                            dropdownClass="b:ls:w-[400px]"
                            countBadge={myNotificationsCount <= 0
                                ? null
                                : myNotificationsCount}
                            onClick={onClickNotifications}
                        >
                            <NotificationsDropdown slot="menu" let:meltItem {meltItem} />
                        </DropdownButtonNavbar>

                        <DropdownButtonNavbar
                            iconClass="tnp tnp-user-alt"
                            dropdownClass="b:ls:w-[260px]"
                            imageId={navbar.me.participantPhoto?.id ?? null}
                            positioning={{ placement: 'bottom-end' }}
                        >
                            <UserDropdown slot="menu" let:meltMenu {meltMenu} {navbar} />
                        </DropdownButtonNavbar>
                    {/if}
                </nav>
            {/if}
        </div>
    </div>
</header>

<header
    class="ls:hidden flex items-center bg-brand-blue h-navbar-mobile z-navbar relative"
>
    <a
        data-sveltekit-reload={false}
        href={urlHelper().homepage({ _country: CountryNull })}
        class="flex items-center pl-[10px] h-full"
    >
        <Picture
            webp={logoGaoAfriqueWebp}
            png={logoGaoAfriquePng}
            class="w-auto h-[48px]"
            alt="Logo GoAfrica"
        />
    </a>
    <div class="flex-1"></div>

    <nav class="flex items-center h-full">
        {#if $withSearchButtonInNavBar}
            <ButtonNavbar
                onClick={(event) => {
                    event.preventDefault();
                    $searchHeaderDisplayOnMobile = !$searchHeaderDisplayOnMobile;
                }}
                srLabel={t.ui.search({}, $lang)}
                iconClass="tnp tnp-search"
            />
        {/if}

        {#if navbar.me == null}
            <div class="px-[2px] ms:px-[4px] mm:px-[8px]">
                <a
                    data-sveltekit-reload={false}
                    href={urlHelper().registration()}
                    class="btn-leo btn-leo-navbar-white btn-lg px-[10px] ms:px-[10px] mm:px-[20px]"
                    >{t.navbar.signup({}, $lang)}</a
                >
            </div>

            <DropdownButtonNavbar
                iconClass="tnp tnp-user-alt"
                positioning={{ placement: 'bottom-end' }}
            >
                <LoginFormDropdown slot="popover" />
            </DropdownButtonNavbar>
        {:else}
            <ButtonNavbar
                htmlElement="a"
                reload={false}
                href={urlHelper().inbox()}
                srLabel={t.navbar.messaging_x({ nb: $nbUnreadThreads }, $lang)}
                iconClass="tnp tnp-envelope-alt"
                countBadge={$nbUnreadThreads <= 0 ? null : $nbUnreadThreads}
            />

            <DropdownButtonNavbar
                iconClass="tnp tnp-bell"
                srLabel={t.navbar.notification({}, $lang)}
                positioning={{ placement: 'bottom' }}
                countBadge={myNotificationsCount <= 0 ? null : myNotificationsCount}
                onClick={onClickNotifications}
            >
                <NotificationsDropdown slot="menu" let:meltItem {meltItem} />
            </DropdownButtonNavbar>

            <DropdownButtonNavbar
                iconClass="tnp tnp-user-alt"
                dropdownClass="w-[260px]"
                imageId={navbar.me.participantPhoto?.id ?? null}
                positioning={{ placement: 'bottom-end' }}
            >
                <UserDropdown slot="menu" let:meltMenu {meltMenu} {navbar} />
            </DropdownButtonNavbar>
        {/if}

        <MainButtonMobile {navbar} {navbarCountryLink} />
    </nav>
</header>
<div class="ls:hidden" id="GoAfricaNext-Navbar-dropdrown-mobile-portal"></div>
